import { Head, Link } from "@inertiajs/react"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/* Icons                                                                */
/* ------------------------------------------------------------------ */

function IconBuilding() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
    </svg>
  )
}

function IconVideoCamera() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
    </svg>
  )
}

function IconChartBar() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
    </svg>
  )
}

function IconBolt() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
    </svg>
  )
}

function IconUsers() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
    </svg>
  )
}

function IconRocket() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-1.757 4.306 4.493 4.493 0 0 0 4.306-1.758M16.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
    </svg>
  )
}

function IconTarget() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/* Chart data                                                           */
/* ------------------------------------------------------------------ */

const VIDEO_ADOPTION = [
  { name: "No video", value: 68 },
  { name: "Using video", value: 32 },
]
const ADOPTION_COLOURS = ["#C41E2A", "#E8E4DF"]

const ENQUIRY_COMPARISON = [
  { name: "Without video", enquiries: 25 },
  { name: "With video", enquiries: 100 },
]

const PLATFORM_CTR = [
  { name: "Facebook", ctr: 7.4 },
  { name: "IG Reels", ctr: 10.2 },
  { name: "IG Stories", ctr: 10.5 },
  { name: "TikTok", ctr: 8.1 },
]

/* ------------------------------------------------------------------ */
/* Big stat                                                             */
/* ------------------------------------------------------------------ */

function BigStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="text-center">
      <p className="font-display text-3xl font-bold tracking-tight text-red sm:text-4xl">
        {value}
      </p>
      <p className="mt-2 text-sm leading-snug text-slate">{label}</p>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Example video links                                                  */
/* ------------------------------------------------------------------ */

const VIDEO_EXAMPLES = [
  { id: "ZGttwJ7k52HK", label: "Example 1" },
  { id: "cw6JKHdyzRZl", label: "Example 2" },
  { id: "x3CKje2qrh7P", label: "Example 3" },
  { id: "p2pnnDEwubEh", label: "Example 4" },
  { id: "XN652z2eYhOx", label: "Example 5" },
]

/* ------------------------------------------------------------------ */
/* Page component                                                       */
/* ------------------------------------------------------------------ */

export default function QuiterightMarket({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      {/* ============================================================ */}
      {/* SLIDE 1 — The UK Proptech Opportunity                         */}
      {/* ============================================================ */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
              The opportunity
            </p>
            <h1 className="mt-3 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
              The UK Proptech Opportunity<span className="text-red">.</span>
            </h1>
            <p className="mt-4 max-w-2xl text-lg text-slate">
              1 million property sales per year. 25,000+ agency branches. Less than a third use video marketing. The gap is the opportunity.
            </p>
          </ScrollReveal>

          {/* Big stats row */}
          <ScrollReveal stagger className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-stone bg-white p-6 shadow-sm">
              <BigStat value="1,000,000" label="UK residential property sales per year" />
              <p className="mt-3 text-center text-xs text-warm-grey">HMRC 2024/25</p>
            </div>
            <div className="rounded-xl border border-stone bg-white p-6 shadow-sm">
              <BigStat value="25,665" label="Active estate agency branches in the UK" />
              <p className="mt-3 text-center text-xs text-warm-grey">Zoopla / Industry 2025</p>
            </div>
            <div className="rounded-xl border border-stone bg-white p-6 shadow-sm">
              <BigStat value="~68%" label="Of agents NOT using video marketing" />
              <p className="mt-3 text-center text-xs text-warm-grey">Industry research consensus</p>
            </div>
            <div className="rounded-xl border border-stone bg-white p-6 shadow-sm">
              <BigStat value="£3-10k" label="Agent commission per sale instruction" />
              <p className="mt-3 text-center text-xs text-warm-grey">@ 1.42% avg, £275k avg price</p>
            </div>
          </ScrollReveal>

          {/* Market context callout */}
          <ScrollReveal className="mt-12">
            <div className="rounded-xl border border-red/20 bg-red/5 p-8">
              <div className="flex items-start gap-4">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-red/10 text-red">
                  <IconChartBar />
                </span>
                <div>
                  <h3 className="font-display text-lg font-bold text-charcoal">
                    800,000 active for-sale listings at any time
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate">
                    25,665 branches with an average of ~31 properties each. Less than 35% have social ad video. The gap between what buyers expect and what agents deliver is the opportunity QuiteRight fills.
                  </p>
                  <p className="mt-3 text-xs font-medium text-warm-grey">2025 Market Data</p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ============================================================ */}
      {/* DATA — Charts                                                  */}
      {/* ============================================================ */}
      <section className="border-t border-stone bg-white px-6 py-24 md:py-32">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
              Data at a glance
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-charcoal sm:text-4xl md:text-5xl">
              The numbers behind the opportunity<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {/* Donut — Video adoption gap */}
            <ScrollReveal className="h-full">
              <div className="flex h-full flex-col rounded-xl border border-stone bg-cream p-6 shadow-sm">
                <h3 className="font-display text-base font-semibold text-charcoal">
                  Video adoption gap
                </h3>
                <p className="mt-1 text-sm text-slate">
                  ~68% of UK agents have no video marketing
                </p>
                <div className="mt-6 min-h-48 flex-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={VIDEO_ADOPTION}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={72}
                        dataKey="value"
                        strokeWidth={0}
                      >
                        {VIDEO_ADOPTION.map((_, idx) => (
                          <Cell key={idx} fill={ADOPTION_COLOURS[idx]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v) => `${v}%`}
                        cursor={{ fill: "rgba(196, 30, 42, 0.08)" }}
                        contentStyle={{
                          background: "#1A1A1A",
                          border: "none",
                          borderRadius: "8px",
                          color: "#F5F2ED",
                          fontSize: "13px",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 flex items-center justify-center gap-5 text-xs text-slate">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block size-2.5 rounded-full bg-red" />
                    No video (68%)
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block size-2.5 rounded-full bg-stone" />
                    Using video (32%)
                  </span>
                </div>
              </div>
            </ScrollReveal>

            {/* Bar — Enquiry comparison */}
            <ScrollReveal className="h-full">
              <div className="flex h-full flex-col rounded-xl border border-stone bg-cream p-6 shadow-sm">
                <h3 className="font-display text-base font-semibold text-charcoal">
                  Enquiry uplift with video
                </h3>
                <p className="mt-1 text-sm text-slate">
                  Listings with video ads get 4x more enquiries
                </p>
                <div className="mt-6 min-h-48 flex-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={ENQUIRY_COMPARISON}
                      barCategoryGap="30%"
                      margin={{ top: 4, right: 4, bottom: 4, left: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DF" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11, fill: "#3A3A3C" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#3A3A3C" }}
                        axisLine={false}
                        tickLine={false}
                        width={35}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(196, 30, 42, 0.08)" }}
                        contentStyle={{
                          background: "#1A1A1A",
                          border: "none",
                          borderRadius: "8px",
                          color: "#F5F2ED",
                          fontSize: "13px",
                        }}
                      />
                      <Bar
                        dataKey="enquiries"
                        radius={[6, 6, 0, 0]}
                        fill="#C41E2A"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </ScrollReveal>

            {/* Bar — Platform CTR */}
            <ScrollReveal className="h-full">
              <div className="flex h-full flex-col rounded-xl border border-stone bg-cream p-6 shadow-sm">
                <h3 className="font-display text-base font-semibold text-charcoal">
                  Click-through rates by platform
                </h3>
                <p className="mt-1 text-sm text-slate">
                  Property ad CTR across social channels
                </p>
                <div className="mt-6 min-h-48 flex-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={PLATFORM_CTR}
                      barCategoryGap="20%"
                      margin={{ top: 4, right: 4, bottom: 4, left: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DF" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11, fill: "#3A3A3C" }}
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#3A3A3C" }}
                        axisLine={false}
                        tickLine={false}
                        unit="%"
                        width={40}
                      />
                      <Tooltip
                        formatter={(v) => `${v}%`}
                        cursor={{ fill: "rgba(196, 30, 42, 0.08)" }}
                        contentStyle={{
                          background: "#1A1A1A",
                          border: "none",
                          borderRadius: "8px",
                          color: "#F5F2ED",
                          fontSize: "13px",
                        }}
                      />
                      <Bar
                        dataKey="ctr"
                        radius={[6, 6, 0, 0]}
                        fill="#C41E2A"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SLIDE 2 — Problem / Solution                                  */}
      {/* ============================================================ */}
      <section className="border-t border-stone bg-charcoal px-6 py-24 md:py-32">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
              Problem &amp; Solution
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-cream sm:text-4xl md:text-5xl">
              Why agents miss the social opportunity<span className="text-red">.</span>
            </h2>
            <p className="mt-4 max-w-2xl text-lg text-warm-grey">
              And how QuiteRight fixes it.
            </p>
          </ScrollReveal>

          <div className="mt-16 grid gap-8 lg:grid-cols-2">
            {/* THE PROBLEM */}
            <ScrollReveal>
              <div className="h-full rounded-xl border border-slate/30 p-8">
                <div className="flex items-center gap-3">
                  <span className="flex size-8 items-center justify-center rounded-full bg-red/20 text-xs font-bold text-red">!</span>
                  <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-red">The Problem</h3>
                </div>
                <ul className="mt-8 space-y-6">
                  {[
                    {
                      icon: IconVideoCamera,
                      title: "Agents have no social ad content",
                      desc: "~68% of agents have no video marketing strategy, yet 73% of vendors prefer agents who use video on Instagram, TikTok, Facebook and YouTube.",
                    },
                    {
                      icon: IconChartBar,
                      title: "Listings are invisible on social",
                      desc: "Instagram Reels and Stories achieve 10%+ CTR on property ads at £0.06 CPC. Most agents are not running any campaigns.",
                    },
                    {
                      icon: IconUsers,
                      title: "Lost instructions to social-savvy rivals",
                      desc: "Listings with video ads get 4x more enquiries. Agents without them lose instructions to competitors already running social campaigns.",
                    },
                    {
                      icon: IconBolt,
                      title: "No time, no tools, no team",
                      desc: "Creating platform-optimised short ads for 4 channels is beyond most agents. They are time-poor and under-resourced.",
                    },
                  ].map((item) => {
                    const Icon = item.icon
                    return (
                      <li key={item.title} className="flex gap-4">
                        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-slate/30 text-warm-grey">
                          <Icon />
                        </span>
                        <div>
                          <h4 className="font-display text-base font-semibold text-cream">{item.title}</h4>
                          <p className="mt-1 text-sm leading-relaxed text-warm-grey">{item.desc}</p>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </div>
            </ScrollReveal>

            {/* THE SOLUTION */}
            <ScrollReveal>
              <div className="h-full rounded-xl border border-red/30 bg-white p-8">
                <div className="flex items-center gap-3">
                  <span className="flex size-8 items-center justify-center rounded-full bg-red text-xs font-bold text-white">
                    <svg viewBox="0 0 20 20" fill="currentColor" className="size-4">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-red">The QuiteRight Solution</h3>
                </div>
                <ul className="mt-8 space-y-6">
                  {[
                    {
                      icon: IconRocket,
                      title: "Short social ads in minutes",
                      desc: "Upload listing photos. QuiteRight generates platform-ready short ads for Instagram, TikTok, Facebook and YouTube. Done in minutes, not days.",
                    },
                    {
                      icon: IconVideoCamera,
                      title: "Built for every platform",
                      desc: "Output auto-formatted for Reels, Stories, Shorts and Feed ads. Agents post directly, with no editing, no agency, no delay.",
                    },
                    {
                      icon: IconTarget,
                      title: "3-click workflow",
                      desc: "Upload photos, choose template, publish. No technical skill needed. Built for busy agents who just want results.",
                    },
                    {
                      icon: IconChartBar,
                      title: "Win more instructions on social proof",
                      desc: "Equip agents with a visible, active social presence. Vendors can see the agent running campaigns before they even call.",
                    },
                  ].map((item) => {
                    const Icon = item.icon
                    return (
                      <li key={item.title} className="flex gap-4">
                        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-red/10 text-red">
                          <Icon />
                        </span>
                        <div>
                          <h4 className="font-display text-base font-semibold text-charcoal">{item.title}</h4>
                          <p className="mt-1 text-sm leading-relaxed text-slate">{item.desc}</p>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* Why Now — 4-card grid                                         */}
      {/* ============================================================ */}
      <section className="border-t border-stone px-6 py-24 md:py-32">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">Why now</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-charcoal sm:text-4xl md:text-5xl">
              The market is ready. Most agents are not<span className="text-red">.</span>
            </h2>
          </ScrollReveal>
          <ScrollReveal stagger className="mt-12 grid gap-5 sm:grid-cols-2">
            {[
              {
                icon: IconChartBar,
                title: "Social ads are now essential for agents",
                desc: "Property ads on Facebook average 7.4% CTR at just £0.06 per click. Instagram Reels and Stories top 10% CTR. Agents need video content for all four platforms now.",
              },
              {
                icon: IconVideoCamera,
                title: "Platforms reward short-form video",
                desc: "Instagram, TikTok, YouTube Shorts and Facebook Reels all prioritise short video in their algorithms. Listing ads in this format reach buyers agents cannot target any other way.",
              },
              {
                icon: IconBolt,
                title: "Technology makes social ads scalable",
                desc: "Professional short-form ads can now be produced at near-zero marginal cost. QuiteRight is purpose-built to bring this to UK estate agents.",
              },
              {
                icon: IconBuilding,
                title: "25,665 branches with no social ad engine",
                desc: "Only ~32% of listings use any video content. Almost no UK agency has a structured pipeline to push listing ads across all four platforms simultaneously.",
              },
            ].map((item) => {
              const Icon = item.icon
              return (
                <div key={item.title} className="rounded-xl border border-stone bg-white p-6 shadow-sm">
                  <span className="flex size-10 items-center justify-center rounded-full bg-cream text-charcoal">
                    <Icon />
                  </span>
                  <h4 className="mt-4 font-display text-base font-semibold text-charcoal">{item.title}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-slate">{item.desc}</p>
                </div>
              )
            })}
          </ScrollReveal>
        </div>
      </section>

      {/* ============================================================ */}
      {/* Examples — real property videos                                */}
      {/* ============================================================ */}
      <section className="border-t border-stone bg-white px-6 py-24 md:py-32">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal className="text-center">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">See it in action</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-charcoal sm:text-4xl md:text-5xl">
              Real listings. Real videos. 60 seconds each<span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-slate">
              Every video below was created from a property listing in under a minute. No filming. No editing. No videographer.
            </p>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {VIDEO_EXAMPLES.map((video, i) => (
              <a
                key={video.id}
                href={`https://www.quiteright.co.uk/v/${video.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center justify-center rounded-xl border border-stone bg-cream p-8 transition-all hover:border-red/30 hover:shadow-lg"
              >
                <span className="flex size-14 items-center justify-center rounded-full bg-red/10 text-red transition-colors group-hover:bg-red group-hover:text-white">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="ml-0.5 size-6">
                    <path d="M8 5.14v14l11-7-11-7z" />
                  </svg>
                </span>
                <p className="mt-4 font-display text-base font-semibold text-charcoal">
                  Watch video {i + 1}
                </p>
                <p className="mt-1 text-xs text-warm-grey">
                  quiteright.co.uk
                </p>
              </a>
            ))}
            <Link
              href="/examples"
              className="group flex flex-col items-center justify-center rounded-xl border border-dashed border-stone p-8 transition-all hover:border-red/30"
            >
              <span className="flex size-14 items-center justify-center rounded-full bg-stone/50 text-slate transition-colors group-hover:bg-red/10 group-hover:text-red">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </span>
              <p className="mt-4 font-display text-base font-semibold text-charcoal">
                View all examples
              </p>
              <p className="mt-1 text-xs text-warm-grey">
                More property videos
              </p>
            </Link>
          </ScrollReveal>
        </div>
      </section>

      {/* ============================================================ */}
      {/* Footer CTA                                                    */}
      {/* ============================================================ */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <ScrollReveal className="mx-auto max-w-3xl text-center">
          <p className="font-display text-3xl font-bold text-cream md:text-4xl">
            3 clicks. Every listing. Every platform<span className="text-red">.</span>
          </p>
          <p className="mx-auto mt-4 max-w-xl text-base text-warm-grey">
            Paste a listing, get a video. The agent who uses these wins more instructions and sells faster.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              See it with your listings
            </Link>
            <Link
              href="/examples"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-6 py-3 text-base font-medium text-cream transition-colors hover:bg-slate/30 sm:w-auto"
            >
              View more examples
            </Link>
          </div>
          <p className="mt-8 text-sm text-warm-grey">
            quiteright.co.uk · team@quiteright.co.uk
          </p>
        </ScrollReveal>
      </section>
    </PublicLayout>
  )
}
